import { isNil } from "lodash";
import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { defaultMargin } from "./constants";
import { yAxisFormatter2 } from "./ins-horizontal-bar-chart-formatter";
import { getScaleLegendText2, onRenderHorizontalBarChart } from "./ins-horizontal-bar-datapoint-chart-helper";
import { useWidgetComponentProps, useCurrentWidgetState, useWidgetCurrentSortBy, useShownValues, useWidgetCurrentDisplayOptions } from "components/widgets/hooks/use-current-widget";
import { CommmonFormatEnum } from "datapoints";
import { useGetDatapointsWidgetData } from "hooks/widget-data/use-datapoints";
import { applyUserSettings } from "hooks/widget-data/user-settings/user-settings";
export const useGetHorizontalBarChartConfig = (props) => {
    const { sectionId, widgetId } = props, { showScale, showTotals } = useWidgetCurrentDisplayOptions(sectionId, widgetId) ?? {}, dispatch = useDispatch(), shownValues = useShownValues(sectionId, widgetId), sortedBy = useWidgetCurrentSortBy(sectionId, widgetId), widgetProps = useWidgetComponentProps(sectionId, widgetId), widgetState = useCurrentWidgetState(sectionId, widgetId);
    const [[data], queryState] = useGetDatapointsWidgetData(props);
    const { format, max, min, sum } = data?.additionalData ?? {}, emptyRightSide = min < 0 && max > 0, series = useMemo(() => {
        if (!data?.data?.some((d) => d?.series)) {
            return [];
        }
        return data?.data?.map((x) => applyUserSettings(x.series, { shownValues, sortBy: sortedBy }, widgetProps.colorSet === "esg", "esg-donut-color", false));
    }, [data?.data, shownValues, sortedBy, widgetProps.colorSet]);
    const options = useMemo(() => ({
        chart: {
            className: "default-horizontal-bar-char",
            events: {
                render() {
                    onRenderHorizontalBarChart(this, widgetProps, dispatch, sectionId, widgetId);
                }
            },
            marginLeft: defaultMargin,
            marginRight: 15,
            plotShadow: false,
            styledMode: true,
            type: "bar"
        },
        credits: {
            enabled: false
        },
        legend: {
            enabled: series.length > 1,
            symbolRadius: 0
        },
        plotOptions: {
            bar: {
                animation: false,
                minPointLength: 5
            }
        },
        series: series.map((x) => ({
            data: x,
            dataLabels: {
                crop: false,
                enabled: isNil(showTotals) ? false : showTotals,
                formatter() {
                    return `<div class="labelName">${this.point.options.custom?.formatted ?? ""}</div>`;
                },
                inside: false,
                overflow: "allow"
            },
            dataSorting: {
                enabled: false
            },
            id: x[0]?.title,
            minPointLength: 0,
            name: x[0]?.title ?? "",
            type: "bar"
        })),
        title: {
            text: ""
        },
        xAxis: {
            categories: series.length === 0 ? [] : series[0]?.map((o) => o.name) || [],
            labels: {
                useHTML: true
            },
            min: 0,
            type: "category"
        },
        yAxis: {
            endOnTick: false,
            gridLineWidth: 2,
            labels: {
                enabled: isNil(showScale) || showScale,
                formatter() {
                    return yAxisFormatter2(this, min < 0, widgetState?.dataGranularityType, format, format === CommmonFormatEnum.percent ? undefined : sum);
                },
                useHTML: true
            },
            plotLines: [{ value: 0 }],
            softMax: max,
            softMin: min,
            startOnTick: false,
            title: {
                text: widgetProps.hideScaleTitle ? "" : `${getScaleLegendText2(format)}${""}`,
                useHTML: true
            },
            visible: isNil(showScale) ? min < 0 : showScale
        }
    }), [series, showTotals, showScale, max, min, format, widgetState?.dataGranularityType, widgetProps, dispatch, sectionId, widgetId, sum]);
    return {
        additionalData: data?.additionalData,
        chartKey: `${widgetState?.shownValues?.filter}-${widgetState?.shownValues?.quantity}-${emptyRightSide}`,
        childrenId: widgetProps.childrenId,
        colorSet: widgetProps.colorSet,
        enableLabels: showTotals || emptyRightSide,
        options,
        queryState,
        series,
        showScale: Boolean(showScale)
    };
};
